import { validateLabelsAndName } from './label-util';
import { METRIC_NAME_MAPPING, METRICS_TO_RECORD } from './metrics';

import type PrometheusBatcher from 'js/src/libs/prometheusBatcher';

/**
 *
 * This singleton class handles registering metrics and timing interactions.
 * See ./metrics.ts for examples on how to define your own metrics.
 *
 * @example
 * import ActivityProfiler from './activityProfiler'
 * // A single instance of this class is created and attached to the global context. The default
 * // export of this file is that instance.
 *
 * @example
 * const uuid = generateUuid();
 * ActivityProfiler.start(uuid);
 * // any code you want to time. it does not have to be synchronous - as long as the caller can supply the
 * // same uuid to the end call, the same event will be timed
 * ActivityProfiler.end(uuid, metricName, eventLabels)
 *
 */
class ActivityProfiler {
    static instance: ActivityProfiler;

    _startTimeMap: Map<string, number>;
    prometheus: typeof PrometheusBatcher | null;

    static getInstance() {
        if (ActivityProfiler.instance) {
            return ActivityProfiler.instance;
        }
        ActivityProfiler.instance = new ActivityProfiler();
        return ActivityProfiler.instance;
    }

    constructor() {
        // maps event IDs to a timestamp that represents when the event started
        this._startTimeMap = new Map();

        // get global PrometheusBatcher instance
        if (window.Prometheus) {
            this.prometheus = window.Prometheus;
        } else {
            this.prometheus = null;
        }

        // creates a metric class instance for each prometheus metric to record
        if (this.prometheus) {
            METRICS_TO_RECORD.forEach((metricDefn) => this.prometheus!.register(metricDefn));
        }
    }

    overrideContext(newContext: string) {
        if (!this.prometheus) {
            return;
        }
        this.prometheus.overrideContext(newContext);
    }

    /**
     * Starts timing the execution of an event, identified by a unique id
     *
     * @param {uuid} id : a unique identifier for the event which should be used to end the timing
     */
    start(id: string) {
        if (!this.prometheus) {
            return;
        }
        if (!id) {
            return;
        }
        this._startTimeMap.set(id, performance.now());
    }

    /**
     * Ends timing for event previously identified by an id. Stores the observed value internally
     * to be sent to prometheus later
     *
     * @param {uuid} id an existing uuid used in a past call to start()
     * @param {string} metricName : a const metric name defined in ./metrics.ts
     * @param {object} eventLabels : key value pairs of labels for the specific metric name. See .metrics.ts for
     *  each event's expected labels and label values.
     */
    async end(id: string, metricName: string, eventLabels: Record<string, string>) {
        if (!this.prometheus) {
            return;
        }

        const end = performance.now();
        // async execute all non-timing logic for performance
        return new Promise<void>((resolve, reject) => {
            if (!this.prometheus) {
                reject(
                    new Error(
                        'ActivityProfiler is not recording performance because Prometheus is not available',
                    ),
                );
                return;
            }
            const startTime = this._startTimeMap.get(id);
            if (startTime === undefined) {
                reject(
                    new Error(
                        "ActivityProfiler received an end timing call for an event that doesn't exist",
                    ),
                );
                return;
            }
            if (!validateLabelsAndName(metricName, eventLabels)) {
                reject(
                    new Error(`
                    ActivityProfiler is not recording performance of ${metricName} because of an unexpected metric name or label set.
                `),
                );
                return;
            }
            const metricDefn = METRIC_NAME_MAPPING[metricName];
            const measuredTime = end - startTime;
            this.prometheus.observe(metricDefn, measuredTime, eventLabels);
            this._startTimeMap.delete(id);
            resolve();
        });
    }

    inc(metricName: string, eventLabels: Record<string, string> = {}) {
        if (!this.prometheus) {
            return;
        }
        const metricDefn = METRIC_NAME_MAPPING[metricName];
        this.prometheus.inc(metricDefn, eventLabels);
    }
}

/**
 * Singleton implementation to export and create only one instance that
 * is attached to the global context
 */

export function getGlobal(): Window & typeof globalThis {
    if (typeof window !== 'undefined') {
        return window;
    } else if (typeof global !== 'undefined') {
        return global as Window & typeof globalThis;
    } else if (typeof self !== 'undefined') {
        return self;
    } else {
        throw new Error('unable to find global context');
    }
}

let profilerInstance: ActivityProfiler;

if (getGlobal().ActivityProfiler) {
    profilerInstance = getGlobal().ActivityProfiler;
} else {
    profilerInstance = ActivityProfiler.getInstance();
    getGlobal().ActivityProfiler = profilerInstance;
}

export default profilerInstance;
